import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentFormState } from "../utils/hosted-agents"

import { AlertTriangle } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"

import { useAdminLibrariesQuery } from "../hooks/admin-queries"

/** Row shape of the admin libraries query (GET /api/admin/navigator/libraries). */
type AdminLibraryRow = {
  id: string
  display_name?: string
  description?: string
  search_enabled?: boolean
}

const LIBRARY_SEARCH_TOOL = "library_search"

// Libraries tab of the hosted-agent edit wizard (P2-2): the agent-level
// knowledge-library GRANT. The gateway seeds `library_search` from this
// binding when the client envelope carries no selection (the iOS engine never
// does), and an envelope may only narrow it. Rows come from the same admin
// libraries query the Libraries card uses; disabled libraries stay listed (a
// binding to a paused library is legitimate config) but are flagged.
// Already-bound ids missing from the catalog stay visible so a save never
// silently drops them.
export function HostedAgentLibrariesTab({
  form,
  patch,
}: {
  form: HostedAgentFormState
  patch: (partial: Partial<HostedAgentFormState>) => void
}) {
  const { t } = useTranslation()
  const librariesQuery = useAdminLibrariesQuery()

  const catalog = useMemo<AdminLibraryRow[]>(() => {
    const data: unknown[] = Array.isArray(librariesQuery.data) ? librariesQuery.data : []
    return data.filter(
      (row): row is AdminLibraryRow =>
        typeof row === "object" && row !== null && typeof (row as AdminLibraryRow).id === "string"
    )
  }, [librariesQuery.data])
  const knownIds = useMemo(() => new Set(catalog.map((row) => row.id)), [catalog])
  const unknownBound = form.libraries.filter((id) => !knownIds.has(id))
  const rows: AdminLibraryRow[] = [...unknownBound.map((id) => ({ id })), ...catalog]

  const needsBinding = form.tools.includes(LIBRARY_SEARCH_TOOL) && form.libraries.length === 0

  const toggle = (id: string, checked: boolean) => {
    patch({
      libraries: checked ? [...form.libraries, id] : form.libraries.filter((entry) => entry !== id),
    })
  }

  const renderRows = () => {
    if (librariesQuery.isLoading) {
      return (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )
    }
    if (rows.length === 0) {
      return (
        <p className="text-muted-foreground text-sm">
          {t("adminDashboard.hostedAgents.librariesTab.empty", {
            defaultValue: "No knowledge libraries are configured yet.",
          })}
        </p>
      )
    }
    return (
      <div className="space-y-2">
        {rows.map((row) => {
          const checked = form.libraries.includes(row.id)
          const unknown = !knownIds.has(row.id)
          return (
            <div key={row.id} className="space-y-2">
              <label
                className="hover:bg-accent/50 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
                htmlFor={`hosted-agent-library-${row.id}`}
              >
                <Checkbox
                  checked={checked}
                  className="mt-0.5"
                  id={`hosted-agent-library-${row.id}`}
                  onCheckedChange={(value) => toggle(row.id, value === true)}
                />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm font-medium">{row.display_name || row.id}</span>
                  <span className="text-muted-foreground font-mono text-xs">{row.id}</span>
                  {row.description ? (
                    <span className="text-muted-foreground text-xs">{row.description}</span>
                  ) : null}
                  {unknown ? (
                    <span className="text-muted-foreground text-xs">
                      {t("adminDashboard.hostedAgents.librariesTab.unknownLibrary", {
                        defaultValue: "Not in the library list",
                      })}
                    </span>
                  ) : null}
                </span>
                {row.search_enabled === false ? (
                  <Badge variant="outline">
                    {t("adminDashboard.hostedAgents.librariesTab.searchDisabled", {
                      defaultValue: "Search disabled",
                    })}
                  </Badge>
                ) : null}
              </label>
              {checked && (
                <div className="mb-3 space-y-2 pl-8">
                  <Label htmlFor={`hosted-agent-library-context-${row.id}`}>
                    {t("adminDashboard.hostedAgents.sources.context")}
                  </Label>
                  <Textarea
                    id={`hosted-agent-library-context-${row.id}`}
                    maxLength={1000}
                    rows={2}
                    value={form.libraryContexts[row.id] ?? ""}
                    onChange={(event) =>
                      patch({
                        libraryContexts: { ...form.libraryContexts, [row.id]: event.target.value },
                      })
                    }
                  />
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.hostedAgents.sources.contextHelp")}
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.hostedAgents.librariesTab.help", {
          defaultValue:
            "Knowledge libraries this agent may search. The selection is bound to the agent itself, so it applies on every client — including ones that never send a library selection. Users still only see libraries they have access to.",
        })}
      </p>
      {needsBinding ? (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.librariesTab.needsBindingTitle", {
              defaultValue: "library_search has nothing to search",
            })}
          </AlertTitle>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.librariesTab.needsBindingDescription", {
              defaultValue:
                "The library_search tool is enabled but no libraries are bound, so it will report that no knowledge libraries are enabled. Select at least one library.",
            })}
          </AlertDescription>
        </Alert>
      ) : null}
      {librariesQuery.isError ? (
        <Alert>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.librariesTab.loadError", {
              defaultValue: "Failed to load the library list.",
            })}
          </AlertDescription>
        </Alert>
      ) : null}
      {renderRows()}
    </div>
  )
}
